import { supabase } from '../supabase'
import { ZipReader, BlobReader, TextWriter } from '@zip.js/zip.js'

export async function readImportZip(file, password) {
  let reader
  try {
    reader = new ZipReader(new BlobReader(file), { password })
    const entries = await reader.getEntries()
    const jsonEntries = entries.filter(e => !e.directory && e.filename.startsWith('json/') && e.filename.endsWith('.json'))
    if (jsonEntries.length === 0) {
      throw new Error('El archivo no contiene una copia de NutriControl (¿es el .zip correcto?)')
    }
    const results = []
    for (const entry of jsonEntries) {
      const text = await entry.getData(new TextWriter())
      results.push(JSON.parse(text))
    }
    return results
  } catch (e) {
    if (e.message?.includes('password') || e.message?.includes('Password')) {
      throw new Error('Contraseña incorrecta')
    }
    throw e
  } finally {
    await reader?.close().catch(() => {})
  }
}

async function findOrCreateFood(food) {
  if (!food?.nombre) return null
  if (food.id) {
    const { data: byId } = await supabase.from('foods').select('id').eq('id', food.id).maybeSingle()
    if (byId) return byId.id
  }
  const { data: byName } = await supabase.from('foods').select('id').ilike('nombre', food.nombre).limit(1)
  if (byName?.length) return byName[0].id
  const { data: created } = await supabase.from('foods').insert({
    nombre: food.nombre,
    kcal_100g: food.kcal_100g,
    proteina_100g: food.proteina_100g,
    grasa_100g: food.grasa_100g,
    carbohidratos_100g: food.carbohidratos_100g,
    fuente: 'importado',
  }).select('id').single()
  return created?.id ?? null
}

function stripRow(row, extra) {
  const { id, paciente_id, profesional_id, registrado_por, ...rest } = row
  return { ...rest, ...extra }
}

// Restaura mediciones, pliegues y actividad física siempre (paciente o
// profesional pueden escribir esas tablas). Las notas clínicas y los
// objetivos solo los puede escribir el profesional (así lo decidimos al
// abrir el acceso al diario de comidas: la RLS no deja que el paciente
// las toque) — por eso solo se restauran cuando se pasa profesionalId,
// y solo si el paciente no tiene ya unos puestos, para no pisar un
// ajuste más reciente con datos de un backup antiguo. No toca el
// registro de comidas: ver importFoodDiary.
export async function importClinicalData(imp, pacienteId, profesionalId) {
  const queries = [
    supabase.from('body_metrics').select('fecha').eq('paciente_id', pacienteId),
    supabase.from('skinfolds').select('fecha').eq('paciente_id', pacienteId),
    supabase.from('activity_logs').select('fecha').eq('paciente_id', pacienteId),
  ]
  if (profesionalId) {
    queries.push(
      supabase.from('clinical_notes').select('fecha_revision, nota').eq('paciente_id', pacienteId),
      supabase.from('patient_goals').select('id').eq('paciente_id', pacienteId),
      supabase.from('activity_profile').select('id').eq('paciente_id', pacienteId),
    )
  }
  const [
    { data: exMetrics },
    { data: exSkinfolds },
    { data: exActivity },
    exNotesRes,
    exGoalsRes,
    exProfileRes,
  ] = await Promise.all(queries)
  const exNotes = exNotesRes?.data
  const exGoals = exGoalsRes?.data
  const exProfile = exProfileRes?.data

  const dateSet = rows => new Set((rows ?? []).map(r => r.fecha))
  const metricDates = dateSet(exMetrics)
  const skinfoldDates = dateSet(exSkinfolds)
  const activityDates = dateSet(exActivity)
  const noteKeys = new Set((exNotes ?? []).map(r => `${r.fecha_revision}|${r.nota}`))

  const counts = { metrics: 0, skinfolds: 0, activity: 0, notes: 0, goals: 0, profile: 0 }

  const newMetrics = (imp.metrics ?? [])
    .filter(m => !metricDates.has(m.fecha))
    .map(m => stripRow(m, { paciente_id: pacienteId }))
  if (newMetrics.length) {
    await supabase.from('body_metrics').insert(newMetrics)
    counts.metrics = newMetrics.length
  }

  const newSkinfolds = (imp.skinfolds ?? [])
    .filter(s => !skinfoldDates.has(s.fecha))
    .map(s => stripRow(s, { paciente_id: pacienteId }))
  if (newSkinfolds.length) {
    await supabase.from('skinfolds').insert(newSkinfolds)
    counts.skinfolds = newSkinfolds.length
  }

  const newActivity = (imp.activityLogs ?? [])
    .filter(a => !activityDates.has(a.fecha))
    .map(a => stripRow(a, { paciente_id: pacienteId }))
  if (newActivity.length) {
    await supabase.from('activity_logs').insert(newActivity)
    counts.activity = newActivity.length
  }

  if (profesionalId) {
    const newNotes = (imp.notes ?? [])
      .filter(n => !noteKeys.has(`${n.fecha_revision}|${n.nota}`))
      .map(n => stripRow(n, { paciente_id: pacienteId, profesional_id: profesionalId }))
    if (newNotes.length) {
      await supabase.from('clinical_notes').insert(newNotes)
      counts.notes = newNotes.length
    }

    if (!exGoals?.length && imp.goals) {
      await supabase.from('patient_goals').insert(stripRow(imp.goals, { paciente_id: pacienteId }))
      counts.goals = 1
    }

    if (!exProfile?.length && imp.activityProfile) {
      await supabase.from('activity_profile').insert(stripRow(imp.activityProfile, { paciente_id: pacienteId }))
      counts.profile = 1
    }
  }

  return counts
}

// Restaura también el registro de comidas día a día. Solo puede
// ejecutarlo la sesión del propio paciente: el profesional no tiene
// permiso de escritura sobre estas tablas (solo lectura).
export async function importFoodDiary(imp, pacienteId, onProgress) {
  const { data: existingLogs } = await supabase.from('daily_logs').select('fecha').eq('paciente_id', pacienteId)
  const existingDates = new Set((existingLogs ?? []).map(r => r.fecha))
  const newLogs = (imp.dailyLogs ?? []).filter(l => !existingDates.has(l.fecha))

  let done = 0
  for (const log of newLogs) {
    const { data: newLog } = await supabase.from('daily_logs').insert({ paciente_id: pacienteId, fecha: log.fecha }).select('id').single()
    for (const meal of log.meals ?? []) {
      const { data: newMeal } = await supabase.from('meals').insert({ daily_log_id: newLog.id, tipo: meal.tipo }).select('id').single()
      for (const item of meal.meal_items ?? []) {
        const foodId = await findOrCreateFood(item.foods)
        if (foodId) {
          await supabase.from('meal_items').insert({ meal_id: newMeal.id, food_id: foodId, cantidad_gramos: item.cantidad_gramos })
        }
      }
    }
    done++
    onProgress?.(done, newLogs.length)
  }
  return { logs: newLogs.length }
}
